import type { MouseEvent } from "react";
import { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import RuleIcon from "@mui/icons-material/Rule";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import type {
  IRegLimitEvaluation,
  IPositionLimits,
  IDutyGroupLimits,
  IRegLimitResult,
  ILimitingFactor,
  IPairing} from "@vmosolutions/oc-interfaces";
import {
  RegLimitStatus,
  RegLimitOutcome
} from "@vmosolutions/oc-interfaces";

type IDutyPeriodLimitsProps = {
  /** Index into each crew's `dutyGroups` array (counts duty periods only, excludes rest periods). */
  dutyPeriodIndex: number;
  regLimits: IRegLimitEvaluation | null;
  loading: boolean;
  storedPairing: IPairing;
  /** True when the pairing has unsaved edits. The evaluation is fetched against the saved pairing only, so its
   *  limits may be stale or misaligned with the edited duty periods; when stale we grey the chip and hide the
   *  numbers rather than present them as current. */
  stale?: boolean;
}

/** One decimal, trailing-zero trimmed: 5.0 -> "5h", 5.2 -> "5.2h". */
const formatHours = (hours: number): string => `${Number(hours.toFixed(1))}h`;

/** Buffer shown with an explicit sign so an exceeded limit (negative) reads clearly. */
const formatBuffer = (hours: number): string => {
  const rounded = Number(hours.toFixed(1));
  return `${rounded >= 0 ? "+" : ""}${rounded}h`;
};

export const DutyPeriodLimits = (props: IDutyPeriodLimitsProps): JSX.Element | null => {
  const { dutyPeriodIndex, regLimits, loading, storedPairing, stale } = props;
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const statusColor = (status: RegLimitStatus): string => {
    switch (status) {
      case RegLimitStatus.Fail:
        return theme.palette.error.main;
      case RegLimitStatus.Pass:
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  // Limiting-factor text turns alarming red once the buffer goes negative (the limit is exceeded); amber while
  // it's still a positive headroom.
  const limitingColor = (bufferHours: number): string =>
    bufferHours < 0 ? theme.palette.error.main : theme.palette.warning.main;

  // Resolve a friendly crew label from the pairing positions, falling back to the position code.
  // `crewId` is undefined for the uncrewed estimate, where there is no person to name.
  const crewLabel = (crew: IPositionLimits["crew"]): string => {
    if (crew.crewId == null || crew.crewId === "") {return crew.position;}
    const match = storedPairing.positions.find((pos) => pos.crew?._id === crew.crewId);
    const user = match?.crew;
    const name = user ? `${user.firstName} ${user.lastName}`.trim() : "";
    return name ? `${crew.position} - ${name}` : crew.position;
  };

  // At least one position has limit data for this duty period.
  const hasData = regLimits?.positions.some((p) => p.dutyGroups.at(dutyPeriodIndex) != null) ?? false;
  const erroredPositions =
    regLimits?.positions.filter((p) => p.status === RegLimitOutcome.Error && p.error != null && p.error !== "") ?? [];
  const hasError = erroredPositions.length > 0;
  const notCheckedPositions = regLimits?.positions.filter((p) => p.status === RegLimitOutcome.NotChecked) ?? [];
  const hasNotChecked = notCheckedPositions.length > 0;
  // The chip has something to say while loading, when limits exist, or when a position errored / wasn't checked.
  if (!loading && !hasData && !hasError && !hasNotChecked) {return null;}
  const errorState = !loading && !hasData && hasError;
  // Unsaved edits make the saved-pairing limits potentially stale/misaligned
  const staleState = stale === true && !loading;

  // Per-position binding constraint for this duty period, surfaced inline in the row (one line per position).
  const inlineLimits =
    regLimits?.positions
      .map((position) => {
        const limitingFactor = position.dutyGroups.at(dutyPeriodIndex)?.limitingFactor;
        return limitingFactor ? { label: crewLabel(position.crew), limitingFactor } : null;
      })
      .filter((entry): entry is { label: string; limitingFactor: ILimitingFactor } => entry !== null) ?? [];

  const handleClick = (event: MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl) && !staleState && (hasData || hasError || hasNotChecked);

  const renderPositionTable = (position: IPositionLimits): JSX.Element | null => {
    const dutyGroup: IDutyGroupLimits | undefined = position.dutyGroups.at(dutyPeriodIndex);
    if (dutyGroup == null) {return null;}
    const { limitingFactor } = dutyGroup;
    return (
      <Box key={position.crew.crewId ?? position.crew.position} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 12, mb: 0.5 }}>{crewLabel(position.crew)}</Typography>
        {/* Only the regulation rows scroll; the crew label above and the "Limiting:" line below stay fixed, and
            the column header sticks to the top of the scroll area. */}
        <TableContainer sx={{ maxHeight: 180, overflow: "auto" }}>
          <Table
            size="small"
            stickyHeader
            sx={{
              "& td, & th": { padding: "2px 8px", fontSize: 11, border: "none" },
              "& thead th": { backgroundColor: theme.palette.background.paper },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Regulation</TableCell>
                <TableCell align="right">Current</TableCell>
                <TableCell align="right">Max</TableCell>
                <TableCell align="right">Buffer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...dutyGroup.regulationResults]
                .sort((a, b) => a.bufferHours - b.bufferHours)
                .map((result: IRegLimitResult) => (
                  <TableRow key={`${result.regId}-${result.regulation}-${result.checkType}-${result.window ?? ""}`}>
                    <TableCell sx={{ color: statusColor(result.status) }}>
                      {`${result.regulation}: ${result.title} ${result.window ?? ""}`}
                    </TableCell>
                    <TableCell align="right">{formatHours(result.currentHours)}</TableCell>
                    <TableCell align="right">{formatHours(result.maxHours)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: result.bufferHours < 0 ? theme.palette.error.main : undefined }}
                    >
                      {formatBuffer(result.bufferHours)}
                    </TableCell>
                  </TableRow>
                ))}
              {/* Engines that failed for this crew. They affect every duty period, so we
                show them as a row in each duty period's table under the crew they failed for. */}
              {(position.engineErrors ?? []).map((engineError) => (
                <Tooltip key={`error-${engineError.regId}`} title={engineError.message}>
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: theme.palette.error.main, cursor: "help" }}>
                      {`${engineError.regId}: limits could not be evaluated`}
                    </TableCell>
                  </TableRow>
                </Tooltip>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {limitingFactor && (
          <Typography sx={{ fontSize: 11, mt: 0.5, color: limitingColor(limitingFactor.bufferHours) }}>
            {`Limiting: ${limitingFactor.regulation} (${formatBuffer(limitingFactor.bufferHours)})`}
          </Typography>
        )}
      </Box>
    );
  };

  // Render positions we don't check (notChecked) as an informational note rather than dropping them.
  const renderNotChecked = (positions: IPositionLimits[]): JSX.Element => (
    <Box>
      {positions.map((position) => (
        <Box key={position.crew.crewId ?? position.crew.position} sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{crewLabel(position.crew)}</Typography>
          <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
            No duty limit checks apply to this position.
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // Render errored positions, grouping the affected crew labels by their (often identical) error message.
  const renderErrors = (positions: IPositionLimits[]): JSX.Element => {
    const labelsByMessage = new Map<string, string[]>();
    positions.forEach((position) => {
      const message = position.error ?? "Unknown error";
      const labels = labelsByMessage.get(message) ?? [];
      labels.push(crewLabel(position.crew));
      labelsByMessage.set(message, labels);
    });
    return (
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: 12, mb: 0.5, color: theme.palette.error.main }}>
          Duty Period limits could not be evaluated
        </Typography>
        {Array.from(labelsByMessage.entries()).map(([message, labels]) => (
          <Box key={message} sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{labels.join(", ")}</Typography>
            <Tooltip title={message}>
              <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary, cursor: "help" }}>
                {`Limits could not be calculated for ${labels.length > 1 ? "these positions" : "this position"}.`}
              </Typography>
            </Tooltip>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Tooltip
        title={
          loading
            ? "Loading Flight Duty Period limits..."
            : staleState
              ? "Duty limits don't reflect your latest changes. Recalculate or save to refresh."
              : errorState
                ? "Flight Duty Period limits could not be evaluated"
                : "View Flight Duty Period limits"
        }
      >
        <Chip
          size="small"
          icon={
            loading ? (
              <CircularProgress size={12} thickness={5} color="inherit" />
            ) : errorState && !staleState ? (
              <ErrorOutlineIcon fontSize="small" />
            ) : (
              <RuleIcon fontSize="small" />
            )
          }
          label={loading ? "Loading..." : errorState && !staleState ? "Limits Unavailable" : "Duty Limits"}
          onClick={loading || staleState ? undefined : handleClick}
          variant={open ? "filled" : "outlined"}
          color={staleState ? "default" : errorState ? "error" : open ? "primary" : "default"}
          sx={{
            marginLeft: "8px",
            cursor: loading || staleState ? "default" : "pointer",
            opacity: staleState ? 0.5 : 1,
            height: 20,
            fontSize: 11,
            verticalAlign: "text-bottom",
          }}
        />
      </Tooltip>
      {!staleState && inlineLimits.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "nowrap", columnGap: 1.5, mt: 0.25, overflow: "hidden" }}>
          {inlineLimits.map(({ label, limitingFactor }) => (
            <Typography
              key={label}
              component="span"
              sx={{
                fontSize: 11,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: limitingColor(limitingFactor.bufferHours),
              }}
            >
              {`${label}: ${limitingFactor.regulation} (${formatBuffer(limitingFactor.bufferHours)})`}
            </Typography>
          ))}
        </Box>
      )}
      {(hasData || hasError || hasNotChecked) && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box sx={{ padding: "10px 12px", minWidth: 320, maxWidth: 420 }}>
            {regLimits?.positions.map((position) => renderPositionTable(position))}
            {hasNotChecked && renderNotChecked(notCheckedPositions)}
            {hasError && renderErrors(erroredPositions)}
          </Box>
        </Popover>
      )}
    </>
  );
};
